import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  FolderKanban,
  HardHat,
  LayoutDashboard,
  MessageCircle,
  Moon,
  Package,
  Plus,
  Sun,
  User as UserIcon,
  Users,
  Wrench,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command.js';
import { useGetProjectsQuery } from '@/features/projects/projectsApi.js';
import { useGetTeamsQuery } from '@/features/teams/teamsApi.js';
import { useTheme } from '@/shared/useTheme.js';
import { FEATURES } from '@/shared/config/features.js';

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NAV_OPTIONS = [
  { value: 'dashboard', label: 'Главная', path: '/dashboard', icon: LayoutDashboard },
  { value: 'projects', label: 'Проекты', path: '/projects', icon: FolderKanban },
  ...(FEATURES.TASKS
    ? [{ value: 'tasks', label: 'Задачи', path: '/tasks', icon: ClipboardList }]
    : []),
  { value: 'rates', label: 'Расценки', path: '/rates', icon: Wrench },
  { value: 'materials', label: 'Материалы', path: '/materials', icon: Package },
  ...(FEATURES.TEAMS ? [{ value: 'teams', label: 'Команды', path: '/teams', icon: Users }] : []),
  ...(FEATURES.MESSENGER
    ? [{ value: 'chat', label: 'Чат', path: '/chat', icon: MessageCircle }]
    : []),
  { value: 'profile', label: 'Профиль', path: '/profile', icon: UserIcon },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const { data: projectsData } = useGetProjectsQuery(undefined, { skip: !open });
  const { data: teamsData } = useGetTeamsQuery(undefined, { skip: !open || !FEATURES.TEAMS });

  const projects = useMemo(() => projectsData?.data?.slice(0, 5) ?? [], [projectsData]);
  const teams = useMemo(() => teamsData?.data?.slice(0, 5) ?? [], [teamsData]);

  // Cmd+K / Ctrl+K toggle
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        (e.key === 'k' || e.key === 'K' || e.key === 'л' || e.key === 'Л') &&
        (e.metaKey || e.ctrlKey)
      ) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const select = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Поиск страниц, проектов, команд…" />
      <CommandList>
        <CommandEmpty>Ничего не найдено</CommandEmpty>

        <CommandGroup heading="Навигация">
          {NAV_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            return (
              <CommandItem
                key={opt.value}
                value={`nav-${opt.value} ${opt.label}`}
                onSelect={() => select(opt.path)}
              >
                <Icon className="text-muted-foreground" />
                <span>{opt.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Действия">
          <CommandItem value="action-new-project" onSelect={() => select('/projects/new')}>
            <Plus className="text-primary" />
            <span>Создать проект</span>
            <CommandShortcut>+</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="action-toggle-theme"
            onSelect={() => {
              toggleTheme();
              onOpenChange(false);
            }}
          >
            {theme === 'dark' ? <Sun className="text-warning" /> : <Moon className="text-info" />}
            <span>{theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}</span>
          </CommandItem>
        </CommandGroup>

        {projects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Последние проекты">
              {projects.map((project) => (
                <CommandItem
                  key={project.id}
                  value={`proj-${project.id} ${project.name}`}
                  onSelect={() => select(`/projects/${project.id}`)}
                >
                  <HardHat className="text-primary" />
                  <span className="truncate">{project.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {teams.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Команды">
              {teams.map((team) => (
                <CommandItem
                  key={team.id}
                  value={`team-${team.id} ${team.name}`}
                  onSelect={() => select(`/teams/${team.id}`)}
                >
                  <Users className="text-primary" />
                  <span className="truncate">{team.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
